import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import imageCompression from "browser-image-compression";
import OutputFormatDropdown from "../components/OutputFormatDropdown";

function bytesToNice(bytes) {
  if (!Number.isFinite(bytes)) return "-";
  const units = ["B", "KB", "MB", "GB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function extFromMime(mime) {
  if (mime === "image/jpeg") return "jpg";
  if (mime === "image/png") return "png";
  if (mime === "image/webp") return "webp";
  return "img";
}

export default function Home() {
  const { t } = useTranslation();
  const [originalFile, setOriginalFile] = useState(null);
  const [compressedBlob, setCompressedBlob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Settings
  const [maxMB, setMaxMB] = useState(1.0);
  const [maxWH, setMaxWH] = useState(1920);
  const [quality, setQuality] = useState(0.8);
  const [output, setOutput] = useState("auto");

  const originalUrl = useMemo(
    () => (originalFile ? URL.createObjectURL(originalFile) : ""),
    [originalFile],
  );
  const compressedUrl = useMemo(
    () => (compressedBlob ? URL.createObjectURL(compressedBlob) : ""),
    [compressedBlob],
  );

  React.useEffect(() => {
    return () => {
      if (originalUrl) URL.revokeObjectURL(originalUrl);
      if (compressedUrl) URL.revokeObjectURL(compressedUrl);
    };
  }, [originalUrl, compressedUrl]);

  const isSupported = (file) => {
    const t = file?.type || "";
    return t === "image/jpeg" || t === "image/png" || t === "image/webp";
  };

  async function handleFile(file) {
    setError("");
    setCompressedBlob(null);

    if (!file) return;

    if (!isSupported(file)) {
      setOriginalFile(null);
      setError(t("compress.errorUnsupported"));
      return;
    }

    setOriginalFile(file);
  }

  async function compressNow() {
    if (!originalFile) return;

    setBusy(true);
    setError("");
    setCompressedBlob(null);

    try {
      let mimeType = undefined;
      if (output === "jpeg") mimeType = "image/jpeg";
      if (output === "webp") mimeType = "image/webp";
      if (output === "png") mimeType = "image/png";

      const options = {
        maxSizeMB: Math.max(0.05, Number(maxMB) || 1),
        maxWidthOrHeight: Math.max(200, Number(maxWH) || 1920),
        useWebWorker: true,
        initialQuality: Math.min(1, Math.max(0.1, Number(quality) || 0.8)),
        fileType: mimeType,
        exifOrientation: 1,
      };

      const compressedFile = await imageCompression(originalFile, options);
      setCompressedBlob(compressedFile);
    } catch (e) {
      setError(e?.message || t("compress.errorFailed"));
    } finally {
      setBusy(false);
    }
  }

  function downloadCompressed() {
    if (!compressedBlob || !originalFile) return;

    const base = originalFile.name.replace(/\.[^.]+$/, "");
    const outMime = compressedBlob.type || originalFile.type;
    const ext = extFromMime(outMime);
    downloadBlob(compressedBlob, `${base}-compressed.${ext}`);
  }

  return (
    <div className="compress-page container">
      <div className="compress-header">
        <h2>{t("compress.title")}</h2>
        <p>{t("compress.subtitle")}</p>
      </div>

      <label
        className="file-upload-zone"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files?.[0];
          handleFile(f);
        }}
      >
        <input
          type="file"
          className="file-upload-zone__input"
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <div className="file-upload-zone__inner">
          <div className="file-upload-zone__icon">
            <i className="fas fa-image"></i>
          </div>
          <span className="file-upload-zone__text">
            {t("compress.dragDrop")}
          </span>
        </div>
      </label>

      {error && <div className="upload-error">{error}</div>}

      <div className="compress-grid">
        <div className="compress-card">
          <h3>{t("compress.settings")}</h3>

          <label>
            {t("compress.targetSize")}
            <input
              type="number"
              step="0.1"
              min="0.05"
              value={maxMB}
              onChange={(e) => setMaxMB(e.target.value)}
            />
          </label>

          <label>
            {t("compress.maxWH")}
            <input
              type="number"
              min="200"
              step="10"
              value={maxWH}
              onChange={(e) => setMaxWH(e.target.value)}
            />
          </label>

          <label>
            {t("compress.quality")}: {Number(quality).toFixed(2)}
            <input
              type="range"
              min="0.1"
              max="1"
              step="0.02"
              value={quality}
              onChange={(e) => setQuality(e.target.value)}
            />
          </label>

          <label>
            {t("compress.outputFormat")}
            <OutputFormatDropdown value={output} onChange={setOutput} />
          </label>

          <button
            className="red-main-button"
            onClick={compressNow}
            disabled={!originalFile || busy}
          >
            {busy ? t("compress.compressing") : t("compress.compress")}
          </button>

          <button
            className="btn-secondary"
            onClick={downloadCompressed}
            disabled={!compressedBlob}
          >
            {t("compress.download")}
          </button>

          <div className="compress-stats">
            <div>
              {t("compress.originalSize")}:{" "}
              <b>{originalFile ? bytesToNice(originalFile.size) : "-"}</b>
            </div>
            <div>
              {t("compress.compressedSize")}:{" "}
              <b>{compressedBlob ? bytesToNice(compressedBlob.size) : "-"}</b>
            </div>
            {originalFile && compressedBlob && (
              <div>
                {t("compress.saved")}:{" "}
                <b>
                  {Math.max(
                    0,
                    (1 - compressedBlob.size / originalFile.size) * 100,
                  ).toFixed(1)}
                  %
                </b>
              </div>
            )}
          </div>
        </div>

        <div className="compress-card">
          <h3>{t("compress.preview")}</h3>

          <div className="preview-grid">
            <div className="preview-box">
              <div className="preview-label">{t("compress.original")}</div>
              <div className="preview-inner">
                {originalUrl ? (
                  <img alt="Original preview" src={originalUrl} />
                ) : (
                  <span className="preview-placeholder">
                    {t("compress.noImage")}
                  </span>
                )}
              </div>
            </div>

            <div className="preview-box">
              <div className="preview-label">{t("compress.compressed")}</div>
              <div className="preview-inner">
                {compressedUrl ? (
                  <img alt="Compressed preview" src={compressedUrl} />
                ) : (
                  <span className="preview-placeholder">
                    {t("compress.notCompressed")}
                  </span>
                )}
              </div>
            </div>
          </div>

          <p className="compress-tip">{t("compress.tip")}</p>
        </div>
      </div>
    </div>
  );
}
